import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import FormStepper from 'src/components/FormStepper/FormStepper';
import DialogTransition from 'src/components/DialogTransition';
import { loadClientContracts } from 'src/store/actions/clientActions';
import ContractStatus from 'src/utils/constants/contracts';
import makeInitialState from './CurrentStep/StepOne/initialState';
import useStyles from './styles';
import CurrentStep from './CurrentStep';

const stepperLabels = ['COMMON.ASSIGN', 'COMMON.CONFIRMATION'];

const AssignTransactionToContractDialog = ({
  open,
  handleClose,
  handleAssignInvoiceToContract,
  transaction
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const initialState = useMemo(() => makeInitialState({}), []);
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initialState);
  const [labels, setLabels] = useState(stepperLabels);

  const resetRef = useRef();

  useEffect(() => {
    setLabels(stepperLabels);
  }, [data]);

  const handleNext = useCallback(
    values => {
      setData({ ...data, ...values });
      setStep(step + 1);
    },
    [setStep, step, setData, data]
  );

  const handleBackOrClose = useCallback(() => {
    switch (step) {
      case 0:
        setData(initialState);
        handleClose();
        break;
      case 1:
        setStep(step - 1);
        break;
      default:
        setStep(step - 1);
        break;
    }
  }, [dispatch, step, setStep, handleClose]);

  useEffect(() => {
    if (open) {
      dispatch(
        loadClientContracts(
          {
            status: [ContractStatus.IN_PROGRESS, ContractStatus.ENDED],
            clientId: transaction.clientId
          },
          false
        )
      );
    }
    return () => {
      setStep(0);
      setData(initialState);
      if (resetRef.current) {
        resetRef.current();
      }
    };
  }, [open]);

  const handleSubmit = useCallback(
    values => {
      handleAssignInvoiceToContract(values);
    },
    [data]
  );

  const handleCloseForm = () => {
    if (resetRef.current) {
      resetRef.current();
    }
    setData(initialState);
    handleClose();
  };

  if (!open) return null;

  return (
    <Box>
      <Dialog
        id="form-new-assignation-dialog"
        classes={{ paper: classes.root }}
        open={open}
        scroll="body"
        TransitionComponent={DialogTransition}
        keepMounted
        disableEscapeKeyDown
      >
        <DialogTitle className={classes.title} id="form-new-asiggnation-title">
          <IconButton
            key="close"
            aria-label="Close"
            color="inherit"
            onClick={() => {
              handleCloseForm();
            }}
            className={classes.closeIcon}
          >
            <CloseIcon />
          </IconButton>
          <Typography className={classes.title} color="primary" variant="h2">
            {t('TRANSACTION.ASSOCIATE_TRANSACTION_TO_CONTRACT')}
          </Typography>
        </DialogTitle>
        <Divider light variant="middle" />
        <DialogContent>
          <FormStepper index={step} labels={labels} />
          <CurrentStep
            resetRef={resetRef}
            activeStep={step}
            data={data}
            onNext={handleNext}
            onBack={handleBackOrClose}
            onSubmit={handleSubmit}
            onCloseForm={handleCloseForm}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

AssignTransactionToContractDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleAssignInvoiceToContract: PropTypes.func.isRequired,
  transaction: PropTypes.object.isRequired
};

export default AssignTransactionToContractDialog;
